"use client"

import { useState, useEffect, useContext } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { AuthContext } from "../context/AuthContext"
import { useLanguage } from "../context/LanguageContext"
import "./Dashboard.css"

const Dashboard = () => {
  const { user } = useContext(AuthContext)
  const { t, direction } = useLanguage()
  const [farms, setFarms] = useState([])
  const [projects, setProjects] = useState([])
  const [filteredProjects, setFilteredProjects] = useState([])
  const [selectedFarmId, setSelectedFarmId] = useState(null)
  const [recentCalculations, setRecentCalculations] = useState([])
  const [weatherAlerts, setWeatherAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState([])

  // eslint-disable-next-line
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        // Fetch user farms
        const farmsRes = await axios.get(`${API_URL}/api/farms`)
        setFarms(farmsRes.data)

        // Fetch user projects
        const projectsRes = await axios.get(`${API_URL}/api/projects`)
        setProjects(projectsRes.data)
        setFilteredProjects(projectsRes.data.slice(0, 5)) // Show only first 5 projects initially

        // Fetch user groups
        const groupsRes = await axios.get(`${API_URL}/api/groups`)
        setGroups(groupsRes.data)

        // Fetch recent WATref calculations
        const calculationsRes = await axios.get(`${API_URL}/api/etref/history?limit=5`)
        setRecentCalculations(calculationsRes.data)

        // Dummy weather alerts for demonstration
        setWeatherAlerts([
          { id: 1, type: "warning", message: "High temperature expected for Farm A", date: new Date() },
          { id: 2, type: "info", message: "Precipitation forecast for Farm B region", date: new Date() },
        ])

        setLoading(false)
      } catch (err) {
        setError("Failed to load dashboard data")
        setLoading(false)
        console.error(err)
      }
    }

    fetchData()
  }, [])

  const handleFarmClick = (farmId) => {
    if (selectedFarmId === farmId) {
      // If clicking the same farm, reset to show all projects
      setSelectedFarmId(null)
      setFilteredProjects(projects.slice(0, 5))
    } else {
      // Filter projects by selected farm
      setSelectedFarmId(farmId)
      const farmProjects = projects.filter((project) => project.farm._id === farmId)
      setFilteredProjects(farmProjects.slice(0, 5))
    }
  }

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>{t("Loading dashboard...")}</p>
      </div>
    )
  }

  return (
    <div className="dashboard-container" dir={direction}>
      <div className="dashboard-header">
        <h1>
          {t("Welcome")}, {user?.name}
        </h1>
        <p className="last-login">
          {t("Last login")}: {new Date().toLocaleString()}
        </p>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card farms-card">
          <div className="card-header">
            <h2>{t("My Farms")}</h2>
            <div className="card-header-actions">
              <Link to="/farm-management" className="card-action-button view-farms-btn">
                <i className="fas fa-list"></i> {t("View Farms")}
              </Link>
              <Link to="/add-farm" className="card-action-button">
                <i className="fas fa-plus"></i> {t("Add Farm")}
              </Link>
            </div>
          </div>
          <div className="card-body">
            {farms.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-seedling empty-icon"></i>
                <p>{t("You don't have any farms yet")}</p>
                <Link to="/add-farm" className="btn btn-primary">
                  {t("Add Your First Farm")}
                </Link>
              </div>
            ) : (
              <div className="farms-list">
                {farms.map((farm) => (
                  <div
                    key={farm._id}
                    className={`farm-item ${selectedFarmId === farm._id ? "selected" : ""}`}
                    onClick={() => handleFarmClick(farm._id)}
                  >
                    <div className="farm-icon">
                      <i className="fas fa-tractor"></i>
                    </div>
                    <div className="farm-details">
                      <h3 className="farm-name">{farm.name}</h3>
                      <p className="farm-location">
                        {farm.district || "not available"}, {farm.country}
                      </p>
                      <p className="farm-area">
                        {t("Area")}: {farm.area} m² ({t("Available")}: {farm.availableArea} m²)
                      </p>
                    </div>
                    <div className="farm-actions">
                      <Link
                        to={`/farms/${farm._id}`}
                        className="farm-action-button"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/farms/${farm._id}/edit`}
                        className="farm-action-button"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card projects-card">
          <div className="card-header">
            <h2>
              {t("My Field")}
              {selectedFarmId && (
                <span className="filter-indicator">
                  - {farms.find((f) => f._id === selectedFarmId)?.name}
                  <button
                    className="clear-filter-btn"
                    onClick={() => handleFarmClick(selectedFarmId)}
                    title={t("Clear filter")}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              )}
            </h2>
            <div className="card-header-actions">
              <Link to="/projects" className="card-action-button view-projects-btn">
                <i className="fas fa-list"></i> {t("View Field")}
              </Link>
              <Link to="/add-project" className="card-action-button">
                <i className="fas fa-plus"></i> {t("New Field")}
              </Link>
            </div>
          </div>
          <div className="card-body">
            {filteredProjects.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-project-diagram empty-icon"></i>
                <p>{selectedFarmId ? t("No Field for this farm yet") : t("You don't have any Field yet")}</p>
                <Link to="/add-project" className="btn btn-primary">
                  {t("Create Your First Field")}
                </Link>
              </div>
            ) : (
              <div className="projects-list">
                {filteredProjects.map((project) => (
                  <div key={project._id} className="project-item">
                    <div className="project-info">
                      <div className="project-name">
                        {project.name}
                        <span className="project-area">{project.allocatedArea} m²</span>
                      </div>
                      <div className="project-farm-name">
                        <i className="fas fa-tractor"></i>
                        {project.farm?.name || "Unknown Farm"}
                      </div>
                      <div className="project-crop">
                        {project.cropType ? project.cropType.name : project.customCropType?.cropType}
                      </div>
                    </div>
                    <div className="project-actions">
                      <Link to={`/projects/${project._id}`} className="project-action-button" title={t("View Field")}>
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/projects/${project._id}/kc-calculator/${project.crop}`}
                        className="project-action-button"
                        title={t("Kc Calculator")}
                      >
                        <i className="fas fa-calculator"></i>
                      </Link>
                    </div>
                  </div>
                ))}
                {projects.length > 5 && (
                  <div className="projects-footer">
                    <p className="projects-count">
                      {t("Showing")} {filteredProjects.length} {t("of")}{" "}
                      {selectedFarmId ? projects.filter((p) => p.farm._id === selectedFarmId).length : projects.length}{" "}
                      {t("projects")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card groups-card">
          <div className="card-header">
            <h2>{t("My Parameter Station")}</h2>
            <div className="card-header-actions">
              <Link to="/groups" className="card-action-button view-groups-btn">
                <i className="fas fa-list"></i> {t("View Station")}
              </Link>
              <Link to="/group" className="card-action-button">
                <i className="fas fa-plus"></i> {t("Add Station")}
              </Link>
            </div>
          </div>
          <div className="card-body">
            {groups.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-layer-group empty-icon"></i>
                <p>{t("You don't have any parameter groups yet")}</p>
                <Link to="/group" className="btn btn-primary">
                  {t("Create Your First Group")}
                </Link>
              </div>
            ) : (
              <div className="groups-list">
                {groups.slice(0, 5).map((group) => (
                  <div key={group._id} className="group-item">
                    <div className="group-icon">
                      <i className="fas fa-layer-group"></i>
                    </div>
                    <div className="group-details">
                      <h3 className="group-name">{group.name}</h3>
                      <p className="group-id">ID: {group.groupId}</p>
                      <p className="group-location">
                        Lat: {Number.parseFloat(group.location.coordinates[1]).toFixed(4)}, Lng:{" "}
                        {Number.parseFloat(group.location.coordinates[0]).toFixed(4)}
                      </p>
                      <p className="group-devices">
                        {t("Devices")}: {group.selectedDevices?.length || 0} |{t("Forecasting Params")}:{" "}
                        {group.apiParams?.length || 0}
                      </p>
                    </div>
                    <div className="group-actions">
                      <Link to={`/groups/${group._id}`} className="group-action-button" title={t("View Group")}>
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link to={`/groups/${group._id}/edit`} className="group-action-button" title={t("Edit Group")}>
                        <i className="fas fa-edit"></i>
                      </Link>
                    </div>
                  </div>
                ))}
                {groups.length > 5 && (
                  <div className="groups-footer">
                    <p className="groups-count">
                      {t("Showing")} 5 {t("of")} {groups.length} {t("groups")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card recent-calculations-card">
          <div className="card-header">
            <h2>{t("Recent Calculations")}</h2>
            <Link to="/etref-history" className="card-action-link">
              {t("View All")}
            </Link>
          </div>
          <div className="card-body">
            {recentCalculations.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-calculator empty-icon"></i>
                <p>{t("No calculation history yet")}</p>
                <Link to="/" className="btn btn-primary">
                  {t("Calculate WATref")}
                </Link>
              </div>
            ) : (
              <div className="calculations-list">
                {recentCalculations.map((calc) => (
                  <div key={calc._id} className="calculation-item">
                    <div className="calculation-date">{new Date(calc.date).toLocaleDateString()}</div>
                    <div className="calculation-location">
                      {calc.location?.district?.name && calc.location?.country?.name
                        ? `${calc.location.district.name}, ${calc.location.country.name}`
                        : calc.location?.coordinates
                          ? `${calc.location.coordinates.lat?.toFixed(2)}, ${calc.location.coordinates.lng?.toFixed(2)}`
                          : "Unknown"}
                    </div>
                    <div className="calculation-models">
                      {calc.models.length} {t("models used")}
                    </div>
                    {/* <div className="calculation-actions">
                      <button className="calculation-action-button">
                        <i className="fas fa-eye"></i>
                      </button>
                    </div> */}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* <div className="dashboard-card weather-card">
          <div className="card-header">
            <h2>{t("Weather Alerts")}</h2>
          </div>
          <div className="card-body">
            {weatherAlerts.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-cloud-sun empty-icon"></i>
                <p>{t("No weather alerts")}</p>
              </div>
            ) : (
              <div className="alerts-list">
                {weatherAlerts.map((alert) => (
                  <div key={alert.id} className={`alert-item alert-${alert.type}`}>
                    <div className="alert-icon">
                      <i className={`fas fa-${alert.type === "warning" ? "exclamation-triangle" : "info-circle"}`}></i>
                    </div>
                    <div className="alert-content">
                      <p className="alert-message">{alert.message}</p>
                      <span className="alert-date">{new Date(alert.date).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div> */}

        <div className="dashboard-card quick-actions-card">
          <div className="card-header">
            <h2>{t("Quick Actions")}</h2>
          </div>
          <div className="card-body">
            <div className="quick-actions-grid">
              <Link to="/" className="quick-action-item">
                <div className="quick-action-icon">
                  <i className="fas fa-calculator"></i>
                </div>
                <span>{t("Calculate WATref")}</span>
              </Link>
              <Link to="/add-farm" className="quick-action-item">
                <div className="quick-action-icon">
                  <i className="fas fa-plus-circle"></i>
                </div>
                <span>{t("Add Farm")}</span>
              </Link>
              <Link to="/add-project" className="quick-action-item">
                <div className="quick-action-icon">
                  <i className="fas fa-project-diagram"></i>
                </div>
                <span>{t("New Field")}</span>
              </Link>
              <Link to="/device-data" className="quick-action-item">
                <div className="quick-action-icon">
                  <i className="fas fa-microchip"></i>
                </div>
                <span>{t("Weather Parameter Device")}</span>
              </Link>
              <Link to="/profile" className="quick-action-item">
                <div className="quick-action-icon">
                  <i className="fas fa-user-cog"></i>
                </div>
                <span>{t("Update Profile")}</span>
              </Link>
              <Link to="/groups" className="quick-action-item">
                <div className="quick-action-icon">
                  <i className="fas fa-layer-group"></i>
                </div>
                <span>{t("Manage Station")}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
